(function () {
  'use strict';

  const page = (window.FoxTheme && window.FoxTheme.page) || {};

  const STORAGE_KEY = 'seo_404_redirects_v1';
  const SIMILARITY_THRESHOLD = 0.3;

  function getData() {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored
        ? JSON.parse(stored)
        : {
            redirects: {},
            productCache: {},
          };
    } catch {
      return { redirects: {}, productCache: {} };
    }
  }

  function saveData(data) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch {
      console.warn('[404 Redirects] Storage failed');
    }
  }

  // Calculate string similarity (Levenshtein distance)
  function similarity(str1, str2) {
    str1 = str1.toLowerCase();
    str2 = str2.toLowerCase();

    const len1 = str1.length;
    const len2 = str2.length;
    const matrix = [];

    for (let i = 0; i <= len1; i++) {
      matrix[i] = [i];
    }
    for (let j = 0; j <= len2; j++) {
      matrix[0][j] = j;
    }

    for (let i = 1; i <= len1; i++) {
      for (let j = 1; j <= len2; j++) {
        const cost = str1[i - 1] === str2[j - 1] ? 0 : 1;
        matrix[i][j] = Math.min(
          matrix[i - 1][j] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j - 1] + cost
        );
      }
    }

    const maxLen = Math.max(len1, len2);
    return maxLen === 0 ? 1 : 1 - matrix[len1][len2] / maxLen;
  }

  // Extract keywords from product title/handle
  function extractKeywords(text) {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .split(/[\s-]+/)
      .filter((word) => word.length > 2);
  }

  // Calculate semantic similarity between products
  function calculateProductSimilarity(handle1, title1, handle2, title2) {
    const handleSim = similarity(handle1, handle2);
    const titleSim = similarity(title1, title2);

    // Keyword overlap
    const keywords1 = extractKeywords(handle1 + ' ' + title1);
    const keywords2 = extractKeywords(handle2 + ' ' + title2);
    const commonKeywords = keywords1.filter((k) => keywords2.includes(k));
    const keywordScore = commonKeywords.length / Math.max(keywords1.length, keywords2.length, 1);

    // Weighted average
    return handleSim * 0.3 + titleSim * 0.4 + keywordScore * 0.3;
  }

  // Cache current page product info
  function cacheCurrentProduct() {
    if (page.template !== 'product' || !page.product) return;

    const data = getData();
    const handle = page.product.handle;
    const title = page.product.title;

    data.productCache[handle] = {
      title: title,
      url: window.location.pathname,
      lastSeen: Date.now(),
    };

    // Cleanup old cache entries (keep last 500)
    const entries = Object.entries(data.productCache);
    if (entries.length > 500) {
      entries.sort((a, b) => b[1].lastSeen - a[1].lastSeen);
      data.productCache = Object.fromEntries(entries.slice(0, 500));
    }

    saveData(data);
  }

  // Find best alternative for 404'd product
  function findBestAlternative(missingHandle) {
    const data = getData();
    const productCache = data.productCache;

    if (Object.keys(productCache).length === 0) {
      return null;
    }

    let bestMatch = null;
    let bestScore = 0;

    Object.entries(productCache).forEach(([handle, info]) => {
      if (handle === missingHandle) return;

      const score = calculateProductSimilarity(
        missingHandle,
        missingHandle.replace(/-/g, ' '),
        handle,
        info.title
      );

      if (score > bestScore && score >= SIMILARITY_THRESHOLD) {
        bestScore = score;
        bestMatch = { handle, ...info, score };
      }
    });

    return bestMatch;
  }

  // Handle 404 page
  function handle404() {
    if (page.template !== '404') return;

    const currentPath = window.location.pathname;
    const match = currentPath.match(/\/products\/([^/?#]+)/);

    if (!match) return;

    const missingHandle = match[1];
    const data = getData();

    // Check if we already have a redirect for this
    if (data.redirects[missingHandle]) {
      const redirect = data.redirects[missingHandle];

      // Verify redirect target still exists
      if (data.productCache[redirect.targetHandle]) {
        window.location.replace(redirect.targetUrl);
        return;
      }
    }

    // Find best alternative
    const alternative = findBestAlternative(missingHandle);

    if (alternative) {
      // Store redirect
      data.redirects[missingHandle] = {
        targetHandle: alternative.handle,
        targetUrl: alternative.url,
        score: alternative.score,
        created: Date.now(),
      };
      saveData(data);

      // Add link rel canonical (removed meta refresh for SEO compliance)
      const canonical = document.createElement('link');
      canonical.rel = 'canonical';
      canonical.href = window.location.origin + alternative.url;
      document.head.appendChild(canonical);

      // Add structured data for moved content
      const structuredData = {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        url: window.location.href,
        relatedLink: window.location.origin + alternative.url,
        significantLink: window.location.origin + alternative.url,
      };

      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(structuredData);
      document.head.appendChild(script);

      // Show user-friendly message before redirect
      const message = document.createElement('div');
      message.style.cssText =
        'position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%); background: white; padding: 2rem; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.15); z-index: 10000; text-align: center; max-width: 400px;';

      const heading = document.createElement('h3');
      heading.style.cssText = 'margin: 0 0 1rem 0;';
      heading.textContent = 'Product Not Found';
      message.appendChild(heading);

      const description = document.createElement('p');
      description.style.cssText = 'margin: 0 0 1.5rem 0;';
      description.textContent = 'We found a similar product for you...';
      message.appendChild(description);

      const countdownWrapper = document.createElement('p');
      countdownWrapper.style.cssText = 'font-size: 0.875rem; color: #666;';
      countdownWrapper.appendChild(document.createTextNode('Redirecting in '));
      const countdownSpan = document.createElement('span');
      countdownSpan.id = 'countdown';
      countdownSpan.textContent = '3';
      countdownWrapper.appendChild(countdownSpan);
      countdownWrapper.appendChild(document.createTextNode(' seconds'));
      message.appendChild(countdownWrapper);

      document.body.appendChild(message);

      let countdown = 3;
      const countdownEl = message.querySelector('#countdown');
      const interval = setInterval(() => {
        countdown--;
        if (countdownEl) countdownEl.textContent = countdown;
        if (countdown <= 0) {
          clearInterval(interval);
          window.location.replace(alternative.url);
        }
      }, 1000);
    }
  }

  // Initialize
  function init() {
    cacheCurrentProduct();
    handle404();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
